import { useCallback, useEffect, useState } from "react";
import { localDateTime } from "../../lib/datetime";
import { useTranslation } from "react-i18next";
import { BadgeCheck, Banknote, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { NeuButton, NeuCard } from "../../components/neu/Neu";
import { announcePayroll, disbursePayroll, payrollCycle } from "../../api/payroll";
import { listBoxes } from "../../api/accounting";
import { useFeatures } from "../../hooks/useFeatures";
import { PayrollReceiptList } from "./PayrollReceiptList";
import { AnnounceControl } from "./AnnounceControl";

// دورة الرواتب: إعلان الجاهزية (مع تحديد صندوق الخصم) ثم الصرف بعد إقرار كل الموظفين.
export const PayrollCycleCard = ({ month, branchId }) => {
  const { t } = useTranslation();
  const { accounting } = useFeatures();
  const [cycle, setCycle] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [cashbox, setCashbox] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    payrollCycle(month, branchId).then(setCycle).catch(() => setCycle(null));
  }, [month, branchId]);

  useEffect(() => {
    load();
    if (accounting) listBoxes().then(setBoxes).catch(() => setBoxes([]));
  }, [load, accounting]);

  const act = async (fn, okKey) => {
    setBusy(true);
    try {
      setCycle(await fn());
      toast.success(t(okKey));
    } catch (error) {
      const code = error?.response?.data?.detail?.code;
      toast.error(code ? t(`payrollCycle.errors.${code}`, t("errors.generic")) : t("errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  if (!cycle) return null;
  const ready = cycle.status === "ready";
  const paid = cycle.status === "paid";

  return (
    <NeuCard data-testid="payroll-cycle-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="label-xs flex items-center gap-2">
          <Megaphone className="h-4 w-4 text-primary" /> {t("payrollCycle.title")}
        </p>
        <span className="text-xs font-black text-primary" data-testid="payroll-cycle-status">
          {cycle.branch_name ? `${cycle.branch_name} · ` : ""}
          {t(`payrollCycle.status.${cycle.status}`)}
          {cycle.employees ? ` · ${cycle.acknowledged}/${cycle.employees} ${t("payrollCycle.acked")}` : ""}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        {accounting && !paid ? (
          <div>
            <p className="label-xs mb-2">{t("payrollCycle.cashbox")}</p>
            <select className="neu-in w-56 rounded-2xl bg-transparent px-3 py-2 text-sm" data-testid="payroll-cashbox"
              value={cashbox} onChange={(e) => setCashbox(e.target.value)}>
              <option value="">{t("payrollCycle.pickCashbox")}</option>
              {boxes.map((b) => <option key={b.id} value={b.id}>{b.name} · {b.balance} {b.currency}</option>)}
            </select>
          </div>
        ) : null}
        {!paid ? (
          <AnnounceControl cycle={cycle} busy={busy} onAnnounce={() => act(() => announcePayroll({
            month, mode: accounting ? "cashbox" : "external",
            cashbox_id: cashbox || null, branch_id: branchId || null,
          }), "payrollCycle.announced")} />
        ) : null}
        {ready ? (
          <NeuButton className="flex items-center gap-2" data-testid="payroll-disburse" disabled={busy}
            onClick={() => act(() => disbursePayroll(month, branchId), "payrollCycle.disbursed")}>
            <Banknote className="h-4 w-4" /> {t("payrollCycle.disburse")}
          </NeuButton>
        ) : null}
        {paid ? (
          <p className="flex items-center gap-2 text-sm font-bold text-primary" data-testid="payroll-paid">
            <BadgeCheck className="h-4 w-4" /> {t("payrollCycle.paidAt", { at: localDateTime(cycle.paid_at) })}
            {cycle.entry_number ? ` · ${cycle.entry_number}` : ""}
          </p>
        ) : null}
      </div>

      {cycle.receipts?.length ? <PayrollReceiptList receipts={cycle.receipts} /> : null}
    </NeuCard>
  );
};
